import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import type { User, IdTokenResult } from 'firebase/auth';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore/lite';
import { FirebaseService } from './firebase';

/**
 * User role types matching backend
 */
export type UserRole = 'admin' | 'brand_manager' | 'brand_viewer' | 'mfg_admin' | 'mfg_viewer';

/**
 * User profile with custom claims
 */
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: UserRole | null;
  brands: string[];
  /** For mfg_admin / mfg_viewer only — internal manufacturer ID
   * (e.g. "ebelong"). Both roles are scoped to a single mfg. */
  mfg_id?: string | null;
  /** For mfg_admin / mfg_viewer only — public opaque code
   * (e.g. "PLT-7K2X"). Carried in the claim so Firestore rules can
   * match against `devices/{x}.plant_code` without an extra
   * round-trip. */
  plant_code?: string | null;
  emailVerified: boolean;
}

/**
 * Authentication service for managing user auth state
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private firebase = inject(FirebaseService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  // Reactive state using signals
  private _user = signal<User | null>(null);
  private _userProfile = signal<UserProfile | null>(null);
  private _loading = signal(true);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly user = this._user.asReadonly();
  readonly userProfile = this._userProfile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed properties
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly isAdmin = computed(() => this._userProfile()?.role === 'admin');
  readonly isBrandManager = computed(() =>
    this._userProfile()?.role === 'admin' || this._userProfile()?.role === 'brand_manager'
  );
  readonly isBrandViewer = computed(() =>
    this._userProfile()?.role !== null
  );
  readonly isMfgAdmin = computed(() =>
    this._userProfile()?.role === 'mfg_admin'
  );
  /** mfg_viewer = read-only counterpart of mfg_admin (scoped to one mfg). */
  readonly isMfgViewer = computed(() =>
    this._userProfile()?.role === 'mfg_viewer'
  );
  /** Either mfg role — useful for "is this user scoped to a manufacturer?" */
  readonly isMfgUser = computed(() => {
    const r = this._userProfile()?.role;
    return r === 'mfg_admin' || r === 'mfg_viewer';
  });

  /**
   * Where to land this user after they sign in. Mfg users go directly
   * to their own manufacturer hub (the only page they can really use);
   * everyone else lands on /admin. Returns null if there's no
   * authenticated profile yet.
   */
  landingRouteForCurrentRole(): string | null {
    const profile = this._userProfile();
    if (!profile) return null;
    if ((profile.role === 'mfg_admin' || profile.role === 'mfg_viewer') && profile.mfg_id) {
      return `/admin/manufacturers/${profile.mfg_id}/edit`;
    }
    return '/admin';
  }

  constructor() {
    // Only set up auth listener in browser
    if (isPlatformBrowser(this.platformId)) {
      this.initAuthListener();
    } else {
      this._loading.set(false);
    }
  }

  /**
   * Initialize Firebase Auth state listener. Auth (and `firebase/auth`) are
   * loaded lazily, so this is async; `_loading` stays true until it resolves.
   */
  private async initAuthListener(): Promise<void> {
    const auth = await this.firebase.getAuth();
    if (!auth) {
      this._loading.set(false);
      return;
    }

    const { onAuthStateChanged } = await import('firebase/auth');
    onAuthStateChanged(auth, async (user) => {
      this._user.set(user);

      if (user) {
        await this.loadUserProfile(user);
      } else {
        this._userProfile.set(null);
      }

      this._loading.set(false);
    });
  }

  /**
   * Load user profile including custom claims
   */
  private async loadUserProfile(user: User): Promise<void> {
    try {
      const tokenResult: IdTokenResult = await user.getIdTokenResult();
      const claims = tokenResult.claims;

      this._userProfile.set({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role: (claims['role'] as UserRole) || null,
        brands: (claims['brands'] as string[]) || [],
        mfg_id: (claims['mfg_id'] as string) || null,
        plant_code: (claims['plant_code'] as string) || null,
        emailVerified: user.emailVerified,
      });

      // Track login in Firestore (non-blocking)
      this.trackLogin(user.uid).catch((err) =>
        console.error('Error tracking login:', err)
      );
    } catch (error) {
      console.error('Error loading user profile:', error);
      this._userProfile.set({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role: null,
        brands: [],
        emailVerified: user.emailVerified,
      });
    }
  }

  /**
   * Track user login in Firestore and cleanup first-login invitations
   */
  private async trackLogin(uid: string): Promise<void> {
    if (!this.firebase.db) return;

    const userRef = doc(this.firebase.db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return;

    // Update last_login_at
    await updateDoc(userRef, {
      last_login_at: serverTimestamp(),
    });

    // Always attempt to cleanup direct-creation invitations for this user
    // This handles both first login and cases where cleanup failed previously
    await this.cleanupDirectCreationInvitations(uid);
  }

  /**
   * Cleanup direct-creation invitations for a user after first login
   * Uses a simple single-field query to avoid composite index requirement
   */
  private async cleanupDirectCreationInvitations(uid: string): Promise<void> {
    if (!this.firebase.db) return;

    try {
      const invitationsRef = collection(this.firebase.db, 'invitations');
      // Use simple query on user_id only, then filter in JavaScript
      // This avoids composite index issues
      const q = query(invitationsRef, where('user_id', '==', uid));
      const snapshot = await getDocs(q);

      const directCreationDocs = snapshot.docs.filter(
        (d) => d.data()['is_direct_creation'] === true
      );

      if (directCreationDocs.length === 0) {
        return;
      }

      const deletePromises = directCreationDocs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);

      console.log(`Cleaned up ${directCreationDocs.length} direct-creation invitation(s) for user ${uid}`);
    } catch (error) {
      console.error('Error cleaning up direct-creation invitations:', error);
      // Don't rethrow - this is a best-effort cleanup
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string): Promise<boolean> {
    const auth = await this.firebase.getAuth();
    if (!auth) {
      this._error.set('Authentication not available');
      return false;
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (error: unknown) {
      this.handleAuthError(error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<boolean> {
    const auth = await this.firebase.getAuth();
    if (!auth) {
      this._error.set('Authentication not available');
      return false;
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      return true;
    } catch (error: unknown) {
      this.handleAuthError(error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Create a new account with email and password
   */
  async signUpWithEmail(email: string, password: string, displayName: string): Promise<boolean> {
    const auth = await this.firebase.getAuth();
    if (!auth) {
      this._error.set('Authentication not available');
      return false;
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      const credential = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      if (credential.user) {
        await updateProfile(credential.user, { displayName });
      }

      return true;
    } catch (error: unknown) {
      this.handleAuthError(error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Process an invitation after signing up
   */
  async processInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    const functions = await this.firebase.getFunctions();
    if (!functions) {
      return { success: false, message: 'Functions not available' };
    }

    try {
      const { httpsCallable } = await import('firebase/functions');
      const processInvitationFn = httpsCallable(functions, 'processInvitation');
      const result = await processInvitationFn({ invitation_id: invitationId });
      const data = result.data as { success: boolean; message: string; role?: string; brands?: string[] };

      // Refresh token to get updated claims
      if (this._user()) {
        await this._user()!.getIdToken(true);
        await this.loadUserProfile(this._user()!);
      }

      return { success: data.success, message: data.message };
    } catch (error: unknown) {
      console.error('Error processing invitation:', error);
      return { success: false, message: this.getErrorMessage(error) };
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<boolean> {
    const auth = await this.firebase.getAuth();
    if (!auth) {
      this._error.set('Authentication not available');
      return false;
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error: unknown) {
      this.handleAuthError(error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    const auth = await this.firebase.getAuth();
    if (!auth) return;

    try {
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  /**
   * Refresh the user's ID token to get updated claims
   */
  async refreshToken(): Promise<void> {
    const user = this._user();
    if (user) {
      await user.getIdToken(true);
      await this.loadUserProfile(user);
    }
  }

  /**
   * Check if user has access to a specific brand
   */
  hasBrandAccess(brandId: string): boolean {
    const profile = this._userProfile();
    if (!profile) return false;
    if (profile.role === 'admin') return true;
    return profile.brands.includes(brandId);
  }

  /**
   * Check if user can manage a specific brand
   */
  canManageBrand(brandId: string): boolean {
    const profile = this._userProfile();
    if (!profile) return false;
    if (profile.role === 'admin') return true;
    if (profile.role !== 'brand_manager') return false;
    return profile.brands.includes(brandId);
  }

  /**
   * Clear any error state
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown): void {
    const message = this.getErrorMessage(error);
    this._error.set(message);
    console.error('Auth error:', error);
  }

  /**
   * Get user-friendly error message from Firebase error
   */
  private getErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'code' in error) {
      const code = (error as { code: string }).code;
      switch (code) {
        case 'auth/invalid-email':
          return 'Invalid email address';
        case 'auth/user-disabled':
          return 'This account has been disabled';
        case 'auth/user-not-found':
          return 'No account found with this email';
        case 'auth/wrong-password':
          return 'Incorrect password';
        case 'auth/invalid-credential':
          return 'Invalid email or password';
        case 'auth/email-already-in-use':
          return 'An account already exists with this email';
        case 'auth/weak-password':
          return 'Password should be at least 6 characters';
        case 'auth/popup-closed-by-user':
          return 'Sign-in popup was closed';
        case 'auth/network-request-failed':
          return 'Network error. Please check your connection';
        case 'auth/too-many-requests':
          return 'Too many attempts. Please try again later';
        default:
          return 'An error occurred. Please try again';
      }
    }
    return 'An unexpected error occurred';
  }
}
